import { useEffect, useReducer } from 'react';
import {
  fetchWorkspacePolicyCheck,
  type PolicyCheckSuccess,
} from '../../shared/agent-consent/domain/policyClient';
import { useAnalytics } from '../../../Analytics/AnalyticsProvider';
import { getToolsetNames, parseMcpPolicyData } from './policyParse';
import {
  MCP_POLICY_ERROR_KIND,
  policyReducer,
  type PolicyState,
} from './policyState';

import type { BitbucketWorkspace } from '#definitions/consent-info';
import type { ConsentToken } from '#definitions/context';

export type UseBitbucketPolicyParams = {
  token: ConsentToken;
  isEnabled: boolean;
  selectedBitbucketWorkspaces: BitbucketWorkspace[];
  onToolsetsLoaded: (toolsetNames: Set<string>) => void;
};

export function useBitbucketPolicy({
  token,
  isEnabled,
  selectedBitbucketWorkspaces,
  onToolsetsLoaded,
}: UseBitbucketPolicyParams): PolicyState {
  const analyticsClient = useAnalytics();
  const [policyState, dispatch] = useReducer(policyReducer, {
    status: 'idle',
  });

  useEffect(() => {
    if (token.serviceType !== 'MCP') return;

    if (!isEnabled || selectedBitbucketWorkspaces.length === 0) {
      dispatch({ type: 'RESET' });
      return;
    }

    const ac = new AbortController();
    dispatch({ type: 'LOADING' });

    void (async () => {
      try {
        const results = await Promise.all(
          selectedBitbucketWorkspaces.map((workspace) =>
            fetchWorkspacePolicyCheck(
              { contextJwt: token.raw, selectedWorkspace: workspace.ari },
              ac.signal,
            ),
          ),
        );

        if (ac.signal.aborted) return;

        const failedResult = results.find((result) => !result.ok);
        if (failedResult && !failedResult.ok) {
          dispatch({
            type: 'ERROR',
            errorMessage: failedResult.errorMessage,
            errorKind: MCP_POLICY_ERROR_KIND.policyFetchFailed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.policyFetchFailed },
          });
          return;
        }

        const successfulResults = results.filter(
          (result): result is PolicyCheckSuccess => result.ok,
        );

        if (successfulResults.some((result) => !result.data.redirectAllowed)) {
          dispatch({
            type: 'ERROR',
            errorMessage: null,
            errorKind: MCP_POLICY_ERROR_KIND.redirectNotAllowed,
          });
          analyticsClient.pushTrackEvent({
            actionSubject: 'mcpPolicyCheck',
            action: 'failed',
            source: 'oauthConsentScreen',
            attributes: { reason: MCP_POLICY_ERROR_KIND.redirectNotAllowed },
          });
          return;
        }

        const byIntent = successfulResults.flatMap(
          (result) => parseMcpPolicyData(result.data).byIntent,
        );
        dispatch({
          type: 'SUCCESS',
          byIntent,
          // Cross-org scope removal is a Trello-only concept.
          crossOrgScopesRemoved: false,
        });
        onToolsetsLoaded(getToolsetNames(byIntent));
      } catch (e) {
        if (
          (e instanceof DOMException && e.name === 'AbortError') ||
          ac.signal.aborted
        )
          return;
        dispatch({
          type: 'ERROR',
          errorMessage: e instanceof Error ? e.message : 'Unexpected error',
          errorKind: MCP_POLICY_ERROR_KIND.unexpected,
        });
      }
    })();

    return () => {
      ac.abort();
    };
  }, [
    token,
    isEnabled,
    selectedBitbucketWorkspaces,
    analyticsClient,
    onToolsetsLoaded,
  ]);

  return policyState;
}
